import moment from 'moment';
import { ensureDexieReady, getDataFromDexie } from '../db/DexieServises';

export const CUSTOM_TABS_STORAGE_KEY = '@custom_tabs_key';

let customTabsStore: any[] = [];

/**
 * Save custom tabs to in-memory store and localStorage
 */
export function saveCustomTabs(responseOrData: any): any[] {
  let records: any[] = [];
  const dataObj = responseOrData?.data?.data || responseOrData?.data || responseOrData;

  if (Array.isArray(dataObj)) {
    records = dataObj;
  } else if (Array.isArray(dataObj?.records)) {
    records = dataObj.records;
  } else if (Array.isArray(dataObj?.data)) {
    records = dataObj.data;
  }

  if (records && Array.isArray(records)) {
    customTabsStore = records;
    try {
      localStorage.setItem(CUSTOM_TABS_STORAGE_KEY, JSON.stringify(records));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('customTabsUpdated'));
      }
    } catch (e) {
      console.error('Error saving custom tabs to localStorage:', e);
    }
  }
  return customTabsStore;
}

/**
 * Load stored custom tabs from memory or localStorage
 */
export function getStoredCustomTabs(): any[] {
  if (customTabsStore.length > 0) return customTabsStore;
  try {
    const stored = localStorage.getItem(CUSTOM_TABS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        customTabsStore = parsed;
        return customTabsStore;
      }
    }
  } catch (e) {
    console.error('Error reading custom tabs from localStorage:', e);
  }
  return [];
}

// Auto-load on module initialization
getStoredCustomTabs();

/**
 * Helper to get all available profile values for URL placeholder replacement
 * Merges localStorage, Dexie database records, and optional contextParams
 */
export async function getProfileValues(contextParams: Record<string, any> = {}): Promise<Record<string, any>> {
  let employee: any = null;
  let user: any = null;
  let position: any = null;
  let orgUnit: any = null;

  try {
    await ensureDexieReady();
    const empData = await getDataFromDexie('Employee');
    employee = empData?.[0] || null;
    const userData = await getDataFromDexie('User');
    user = userData?.[0] || null;
    const posData = await getDataFromDexie('Position');
    position = posData?.[0] || null;
    const orgData = await getDataFromDexie('OrgUnit');
    orgUnit = orgData?.[0] || null;
  } catch (err) {
    // Dexie may not be ready or tables may not exist yet, fallback to localStorage
  }

  const token =
    localStorage.getItem('appToken') ||
    localStorage.getItem('apptoken') ||
    localStorage.getItem('token') ||
    '';

  const rawBaseurl = localStorage.getItem('serverUrl') || 'https://sfa.nitiapp.com';
  const baseurl = rawBaseurl.trim().replace(/\/+$/, '');

  const employeeId =
    employee?.EmployeeId ||
    localStorage.getItem('empId') ||
    localStorage.getItem('employee_id') ||
    '';

  const positionId =
    employee?.PositionId ||
    position?.PositionId ||
    localStorage.getItem('positionId') ||
    localStorage.getItem('position_id') ||
    '';

  const orgUnitId =
    employee?.OrgUnitId ||
    orgUnit?.OrgUnitId ||
    localStorage.getItem('orgUnitId') ||
    localStorage.getItem('org_unit_id') ||
    '';

  let employeeCode =
    employee?.EmployeeCode ||
    employee?.employee_code ||
    employee?.EmpCode ||
    employee?.empcode ||
    localStorage.getItem('employeeCode') ||
    localStorage.getItem('employee_code') ||
    localStorage.getItem('empCode') ||
    localStorage.getItem('empcode') ||
    '';

  if (!employeeCode) {
    try {
      const storedEmp = JSON.parse(localStorage.getItem('employee') || '{}');
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      employeeCode =
        storedEmp?.EmployeeCode ||
        storedEmp?.employee_code ||
        storedEmp?.EmpCode ||
        storedEmp?.empcode ||
        storedUser?.EmployeeCode ||
        storedUser?.employee_code ||
        '';
    } catch (e) {
      // ignore
    }
  }

  const companyId =
    employee?.CompanyId ||
    orgUnit?.CompanyId ||
    localStorage.getItem('company_id') ||
    '';

  const userId =
    user?.UserId ||
    localStorage.getItem('userId') ||
    localStorage.getItem('user_id') ||
    '';

  const userName =
    user?.Username ||
    user?.Name ||
    employee?.EmployeeName ||
    employee?.FirstName ||
    localStorage.getItem('userName') ||
    '';

  const profileValues: Record<string, any> = {
    // User profile data
    user_mobile: employee?.Phone || employee?.MobileNo || user?.Phone || '',
    employee_code: employeeCode,
    empcode: employeeCode,
    emp_code: employeeCode,
    employeecode: employeeCode,
    employee_email: employee?.Email || user?.Email || '',
    position_id: positionId,
    position_code: position?.PositionCode || '',
    user_name: userName,
    company_id: companyId,
    territory_id: employee?.TerritoryId || '',
    employee_id: employeeId,
    org_unit_id: orgUnitId,

    // Additional profile fields
    branch_id: employee?.BranchId || '',
    designation_id: employee?.DesignationId || '',
    grade_id: employee?.GradeId || '',
    town_id: employee?.Itownid || employee?.TownId || '',
    town_name: employee?.TownName || '',
    unit_name: orgUnit?.UnitName || '',
    reporting_to: employee?.ReportingTo || '',
    user_id: userId,

    token: token,
    apptoken: token,
    baseurl: baseurl,
    serverurl: baseurl,

    // Dynamic date and action placeholders
    month: moment().format('M'), // 1-12
    year: moment().format('YYYY'),
    moye: moment().format('MMM YYYY'), // e.g. Aug 2026
    lastmonth: moment().subtract(1, 'months').format('M'),
    lastmoye: moment().subtract(1, 'months').format('MMM YYYY'),
    action: ''
  };

  return { ...profileValues, ...contextParams };
}

/**
 * Replace placeholders like {employee_id}, {apptoken}, {month}, {year} in URL
 */
export function replacePlaceholders(url: string, values: Record<string, any>): string {
  if (!url) return url;

  let out = url;
  Object.keys(values).forEach((key) => {
    // Try with proper format first: {key}
    const token = `{${key}}`;
    if (out.includes(token)) {
      const val = values[key] ?? '';
      out = out.split(token).join(encodeURIComponent(String(val)));
    }
    // Also handle incomplete placeholder without closing brace: {key (at end of string)
    const incompleteToken = `{${key}`;
    if (out.includes(incompleteToken) && !out.includes(token)) {
      const val = values[key] ?? '';
      out = out.split(incompleteToken).join(encodeURIComponent(String(val)));
    }
  });

  return out;
}

/**
 * Normalize URL with protocol if relative
 */
export function normalizeUrl(rawUrl: string): string {
  let u = (rawUrl || '').trim();
  if (!u) return u;
  const hasProtocol = /^https?:\/\//i.test(u);
  if (!hasProtocol) {
    const base = localStorage.getItem('serverUrl') || 'https://sfa.nitiapp.com/';
    const baseUrl = base ? base.replace(/\/$/, '') : '';
    if (baseUrl) {
      u = u.startsWith('/') ? `${baseUrl}${u}` : `${baseUrl}/${u}`;
    } else {
      u = `https://${u}`;
    }
  }
  return u;
}

export interface CustomTabItem {
  id: string;
  customTabId?: number | string;
  name: string;
  order: number;
  type: string;
  url: string;
  rawUrl: string;
  source: 'custom';
  tabModule?: string;
  tabSubModule?: string;
}

/**
 * Get custom tabs for a module (e.g. 'lms' or 'me') with placeholder replacement applied
 */
export async function getCustomTabsForModule(
  tabModule: string,
  contextParams: Record<string, any> = {}
): Promise<CustomTabItem[]> {
  try {
    const allTabs = getStoredCustomTabs();
    const targetModule = (tabModule || '').toLowerCase();

    // Show all active tabs regardless of TabModule
    const filtered = allTabs.filter((item: any) => {
      const status = String(item.Status ?? item.status ?? '').trim();
      const isActive = status === '1' || status === 'true';
      return isActive;
    });

    // Sort by TabOrder
    filtered.sort((a: any, b: any) => (Number(a.TabOrder) || 0) - (Number(b.TabOrder) || 0));

    const values = await getProfileValues(contextParams);

    const mapped: CustomTabItem[] = filtered.map((item: any, idx: number) => {
      const originalUrl = item.TabUrlApi || item.tab_url_api || '';
      const replaced = replacePlaceholders(originalUrl, values);
      const finalUrl = normalizeUrl(replaced);

      return {
        id: `custom_${item.CustomTabId || item.id || idx}`,
        customTabId: item.CustomTabId || item.id,
        name: item.TabName || item.tab_name || 'Custom Tab',
        order: Number(item.TabOrder) || 0,
        type: item.TabType || 'webview',
        url: finalUrl,
        rawUrl: originalUrl,
        source: 'custom',
        tabModule: item.TabModule,
        tabSubModule: item.TabSubModule,
      };
    });

    return mapped;
  } catch (e) {
    console.error('Error getting custom tabs for module:', e);
    return [];
  }
}
